import {
  CONVEYOR_COST,
  SLOT_REFUND_RATE,
  SUBJECT_COST,
  SUBSCRIBER_COST,
  intervalPeriodCost,
  ofSlotCost,
  takeCost,
} from '../content/economy';
import { MACHINE_DEFS, machineInputCount } from '../content/machine-defs';
import type { MaterialId } from '../content/materials';
import { RECIPES } from '../content/recipes';
import type { SellMethodName } from '../content/market';
import type { RecipeId } from '../content/recipes';
import { CREATION_KINDS } from '../content/creation-kinds';
import type { CreationKind } from '../content/creation-kinds';
import { findBaseEntryConveyor, isBaseEntryPoint, isInBaseBuffer, isInsideBase } from './base';
import type { MachineEntity, MachineKind, ObservableEntity } from './entities';
import { computeEvalOrder } from './eval-order';
import type { SimEvent } from './events';
import { findEntityAt, findSource, inBounds } from './grid';
import { releaseMachinePackets } from './machine-packets';
import { createPort } from './port';
import { feederDirection, machinePorts, poweredConveyors } from './routing';
import type { SimState } from './state';
import { createSubscription } from './subscriber';
import { nextSubjectName, subjectNames } from './subject';
import { toggleSubscribe } from './subscription';
import { DIRECTIONS, translate } from './types';
import type { Direction, EntityId, GridPos, PacketId } from './types';

export type BuildRequest =
  | { type: 'conveyor'; direction: Direction }
  | { type: 'machine'; kind: MachineKind }
  | { type: 'observable'; kind: CreationKind }
  | { type: 'subject' }
  | { type: 'subscriber' };

export type PlaceResult =
  | { ok: true; events?: SimEvent[] }
  | {
      ok: false;
      reason:
        | 'out-of-bounds'
        | 'occupied'
        | 'not-a-source'
        | 'insufficient-cash'
        | 'machine-port-taken'
        | 'inside-base'
        | 'base-buffer-restricted'
        | 'base-entry-taken'
        | 'requires-pipe'
        | 'source-reserved'
        | 'already-placed';
    };

/** No `zip` variant: a join's arguments are the streams wired into its sides, not a stored config. */
export type ConfigUpdate =
  | { kind: 'map'; recipeId: RecipeId }
  | { kind: 'filter'; allow: MaterialId[] }
  | { kind: 'take'; count: number }
  | { kind: 'takeUntil'; subjectId: EntityId }
  | { kind: 'switchMap'; method: SellMethodName };

export type ReconfigureResult =
  | { ok: true; costDelta: number }
  | { ok: false; reason: 'not-a-machine' | 'kind-mismatch' | 'insufficient-cash' };

export type BuyObservableSlotResult =
  | { ok: true; cost: number }
  | { ok: false; reason: 'not-a-observable' | 'kind-mismatch' | 'insufficient-cash' };

export type RemoveObservableSlotResult =
  | { ok: true; refund: number }
  | { ok: false; reason: 'not-a-observable' | 'kind-mismatch' | 'nothing-to-remove' };

export type SetObservablePeriodResult =
  | { ok: true; costDelta: number }
  | {
      ok: false;
      reason: 'not-a-observable' | 'kind-mismatch' | 'unavailable-period' | 'insufficient-cash';
    };

export type SetObservableCompletesToResult =
  { ok: true } | { ok: false; reason: 'not-a-observable' | 'no-such-subject' };

export type EraseResult =
  { ok: true; refund: number; events: SimEvent[] } | { ok: false; reason: 'empty' | 'protected' };

type MachineCostInput =
  | { kind: 'map'; recipeId: RecipeId }
  | { kind: 'filter' }
  | { kind: 'take'; count: number }
  | { kind: 'takeUntil' }
  | { kind: 'switchMap' }
  | { kind: 'zip' }
  | { kind: 'combineLatest' };

/** Single source of truth for pricing a machine kind — by its default config, a placed entity's own
 * config, or a pending {@link ConfigUpdate} — since all three only ever vary in `kind`/`recipeId`/`count`. */
function costFor(input: MachineCostInput): number {
  if (input.kind === 'map') return RECIPES[input.recipeId].cost;
  if (input.kind === 'take') return takeCost(input.count);
  return MACHINE_DEFS[input.kind].cost!;
}

/**
 * A machine is placed unconfigured (see {@link MachineEntity.config}) but still charged its
 * cheapest possible configuration up front, same as before — that keeps toolbar unlock-gates,
 * refunds, and reconfigure deltas consistent even though no config is actually chosen yet.
 * {@link reconfigureMachine} only charges the *extra* delta above this placeholder.
 */
function unconfiguredMachineCost(kind: MachineKind): number {
  if (kind === 'map') return costFor({ kind, recipeId: MACHINE_DEFS.map.availableRecipes![0] });
  if (kind === 'take') return costFor({ kind, count: MACHINE_DEFS.take.availableCounts![0] });
  return costFor({ kind });
}

export function buildCost(request: BuildRequest): number {
  if (request.type === 'conveyor') return CONVEYOR_COST;
  if (request.type === 'observable') return CREATION_KINDS[request.kind].cost;
  if (request.type === 'subject') return SUBJECT_COST;
  if (request.type === 'subscriber') return SUBSCRIBER_COST;
  return unconfiguredMachineCost(request.kind);
}

export function machineCost(machine: MachineEntity): number {
  if (machine.kind === 'map') {
    return machine.config
      ? costFor({ kind: machine.kind, recipeId: machine.config.recipeId })
      : unconfiguredMachineCost('map');
  }
  if (machine.kind === 'take') {
    return machine.config
      ? costFor({ kind: machine.kind, count: machine.config.count })
      : unconfiguredMachineCost('take');
  }
  return costFor({ kind: machine.kind });
}

/** A `costDelta` that comes out negative (downgrading to a cheaper config) is added back to cash
 * as a refund, same arithmetic as a positive delta being charged — there's no separate refund path. */
export function reconfigureMachine(
  state: SimState,
  pos: GridPos,
  update: ConfigUpdate,
): ReconfigureResult {
  const target = findEntityAt(state, pos);
  if (!target || target.kind !== 'machine') return { ok: false, reason: 'not-a-machine' };
  const machine = state.machines[target.id];
  if (machine.kind !== update.kind) return { ok: false, reason: 'kind-mismatch' };

  const costDelta = costFor(update) - machineCost(machine);
  if (state.economy.cash < costDelta) return { ok: false, reason: 'insufficient-cash' };

  if (update.kind === 'map') machine.config = { recipeId: update.recipeId };
  else if (update.kind === 'filter') machine.config = { allow: update.allow };
  else if (update.kind === 'takeUntil') machine.config = { subjectId: update.subjectId };
  else if (update.kind === 'switchMap') machine.config = { method: update.method };
  else machine.config = { count: update.count };

  state.economy.cash -= costDelta;
  return { ok: true, costDelta };
}

/**
 * Buying a slot buys an *empty* argument: which material fills it is left to the source's odds at
 * the moment the observable emits it, so the same slot can come out different on the next
 * subscription. Nothing here draws a material — see `stepObservable`.
 */
export function buyObservableSlot(state: SimState, pos: GridPos): BuyObservableSlotResult {
  const target = findEntityAt(state, pos);
  if (!target || target.kind !== 'observable') return { ok: false, reason: 'not-a-observable' };
  const observable = state.observables[target.id];
  if (observable.kind !== 'of') return { ok: false, reason: 'kind-mismatch' };

  const cost = ofSlotCost(observable.slotCosts.length);
  if (state.economy.cash < cost) return { ok: false, reason: 'insufficient-cash' };

  observable.slotCosts.push(cost);
  state.economy.cash -= cost;
  return { ok: true, cost };
}

/** Only the most recent slot is removable, and only before the observable has emitted it. */
export function removeObservableSlot(state: SimState, pos: GridPos): RemoveObservableSlotResult {
  const target = findEntityAt(state, pos);
  if (!target || target.kind !== 'observable') return { ok: false, reason: 'not-a-observable' };
  const observable = state.observables[target.id];
  if (observable.kind !== 'of') return { ok: false, reason: 'kind-mismatch' };
  if (observable.cursor >= observable.slotCosts.length)
    return { ok: false, reason: 'nothing-to-remove' };

  const paid = observable.slotCosts.pop()!;
  const refund = paid * SLOT_REFUND_RATE;
  state.economy.cash += refund;
  return { ok: true, refund };
}

/** What a placed observable is worth back: its tile, plus whatever the chosen period cost on top. */
export function observableCost(observable: ObservableEntity): number {
  const period = observable.periodTicks;
  return CREATION_KINDS[observable.kind].cost + (period === null ? 0 : intervalPeriodCost(period));
}

/** A negative `costDelta` (stepping back to a slower, cheaper period) is refunded by the same
 * arithmetic that charges a positive one — see {@link reconfigureMachine}. */
export function setObservablePeriod(
  state: SimState,
  pos: GridPos,
  periodTicks: number,
): SetObservablePeriodResult {
  const target = findEntityAt(state, pos);
  if (!target || target.kind !== 'observable') return { ok: false, reason: 'not-a-observable' };
  const observable = state.observables[target.id];
  if (observable.kind !== 'interval') return { ok: false, reason: 'kind-mismatch' };
  if (!CREATION_KINDS.interval.availablePeriodTicks!.includes(periodTicks)) {
    return { ok: false, reason: 'unavailable-period' };
  }

  const current = observable.periodTicks;
  const costDelta =
    intervalPeriodCost(periodTicks) - (current === null ? 0 : intervalPeriodCost(current));
  if (state.economy.cash < costDelta) return { ok: false, reason: 'insufficient-cash' };

  observable.periodTicks = periodTicks;
  state.economy.cash -= costDelta;
  return { ok: true, costDelta };
}

/**
 * Wiring a completion to a channel is free: nothing is built, nothing flows, and the whole point of
 * a name is that using it costs no more than not using it. Pass `null` to unwire.
 */
export function setObservableCompletesTo(
  state: SimState,
  pos: GridPos,
  subjectId: EntityId | null,
): SetObservableCompletesToResult {
  const target = findEntityAt(state, pos);
  if (!target || target.kind !== 'observable') return { ok: false, reason: 'not-a-observable' };
  if (subjectId !== null && !state.subjects[subjectId]) {
    return { ok: false, reason: 'no-such-subject' };
  }

  state.observables[target.id].completesTo = subjectId;
  return { ok: true };
}

/**
 * How many streams may feed the entity at `pos`. Only a machine can take more than one, and only
 * `zip` is one today; an observable is a stream's origin and never accepts an input at all, which
 * the existing "already has a feeder" check below covers.
 */
function inputCapacityAt(state: SimState, pos: GridPos, placingKind?: MachineKind): number {
  if (placingKind) return machineInputCount(placingKind);
  const entity = findEntityAt(state, pos);
  return entity?.kind === 'machine' ? machineInputCount(state.machines[entity.id].kind) : 1;
}

function conveyorConflictsWithMachinePorts(
  state: SimState,
  pos: GridPos,
  direction: Direction,
): boolean {
  for (const dir of DIRECTIONS) {
    const neighborPos = translate(pos, dir);
    const neighbor = findEntityAt(state, neighborPos);
    if (!neighbor || (neighbor.kind !== 'machine' && neighbor.kind !== 'observable')) continue;

    const pointsAtMachine = dir === direction;
    const ports = machinePorts(state, neighborPos);
    const capacity = neighbor.kind === 'machine' ? inputCapacityAt(state, neighborPos) : 1;
    if (pointsAtMachine ? ports.inCount >= capacity : ports.outCount > 0) return true;
  }
  return false;
}

function entityPortConflict(state: SimState, pos: GridPos, placingKind?: MachineKind): boolean {
  const { inCount, outCount } = machinePorts(state, pos);
  return inCount > inputCapacityAt(state, pos, placingKind) || outCount > 1;
}

export function canPlace(state: SimState, request: BuildRequest, pos: GridPos): PlaceResult {
  if (!inBounds(state, pos)) return { ok: false, reason: 'out-of-bounds' };
  if (request.type === 'subscriber' && state.base !== null) {
    return { ok: false, reason: 'already-placed' };
  }
  if (isInsideBase(state.base, pos)) return { ok: false, reason: 'inside-base' };
  if (request.type !== 'conveyor' && isInBaseBuffer(state.base, pos)) {
    return { ok: false, reason: 'base-buffer-restricted' };
  }
  const occupant = findEntityAt(state, pos);
  if (request.type === 'subscriber') {
    if (occupant) return { ok: false, reason: 'occupied' };
    if (findSource(state, pos)) return { ok: false, reason: 'source-reserved' };
    if (feederDirection(state, pos) === null) return { ok: false, reason: 'requires-pipe' };
  } else if (request.type === 'machine') {
    if (!occupant) return { ok: false, reason: 'requires-pipe' };
    if (occupant.kind !== 'conveyor') return { ok: false, reason: 'occupied' };
  } else if (occupant) {
    return { ok: false, reason: 'occupied' };
  } else if (
    (request.type === 'conveyor' || request.type === 'subject') &&
    findSource(state, pos)
  ) {
    return { ok: false, reason: 'source-reserved' };
  }
  if (request.type === 'observable' && !findSource(state, pos)) {
    return { ok: false, reason: 'not-a-source' };
  }
  if (state.economy.cash < buildCost(request)) return { ok: false, reason: 'insufficient-cash' };
  // A Subject attaches to nothing, so no port rule below can have anything to say about it.
  if (request.type === 'subject') return { ok: true };
  if (
    request.type === 'conveyor'
      ? conveyorConflictsWithMachinePorts(state, pos, request.direction)
      : entityPortConflict(state, pos, request.type === 'machine' ? request.kind : undefined)
  ) {
    return { ok: false, reason: 'machine-port-taken' };
  }
  if (
    request.type === 'conveyor' &&
    isBaseEntryPoint(state, pos, request.direction) &&
    findBaseEntryConveyor(state) !== undefined
  ) {
    return { ok: false, reason: 'base-entry-taken' };
  }
  return { ok: true };
}

function createMachineEntity(
  id: EntityId,
  position: GridPos,
  kind: MachineKind,
  pipeDirection: Direction | undefined,
): MachineEntity {
  const inputCount = machineInputCount(kind);
  const base = {
    id,
    position,
    inputs: Array.from({ length: inputCount }, () => createPort(1)),
    outputs: [createPort(1)],
    pipeDirection,
  };
  if (kind === 'combineLatest') {
    return {
      ...base,
      kind,
      internal: {
        latest: Array.from({ length: inputCount }, () => null),
        nextPort: 0,
        wasSubscribed: false,
      },
    };
  }
  if (kind === 'switchMap') {
    return {
      ...base,
      kind,
      config: null,
      internal: { pending: null },
    };
  }
  if (kind === 'map' || kind === 'filter' || kind === 'zip' || kind === 'takeUntil') {
    return { ...base, internal: undefined, kind, config: null };
  }
  return {
    ...base,
    kind,
    config: null,
    internal: { passed: 0, observableWasSubscribed: false },
  };
}

export function place(state: SimState, request: BuildRequest, pos: GridPos): PlaceResult {
  const check = canPlace(state, request, pos);
  if (!check.ok) return check;

  const events: SimEvent[] = [];
  let pipeDirection: Direction | undefined;
  if (request.type === 'machine') {
    const occupant = findEntityAt(state, pos)!;
    if (poweredConveyors(state).has(occupant.id)) events.push(...toggleSubscribe(state));
    const conveyor = state.conveyors[occupant.id];
    pipeDirection = conveyor.direction;
    if (conveyor.slot !== null) {
      events.push({ type: 'packetDespawned', packetId: conveyor.slot });
      delete state.packets[conveyor.slot];
    }
    delete state.conveyors[occupant.id];
  }

  const id = state.nextEntityId++;
  if (request.type === 'conveyor') {
    state.conveyors[id] = { id, position: pos, direction: request.direction, slot: null };
  } else if (request.type === 'observable') {
    const source = findSource(state, pos)!;
    state.observables[id] = {
      id,
      position: pos,
      kind: request.kind,
      sequence: [],
      slotCosts: [],
      yields: source.yields.map((y) => ({ ...y })),
      periodTicks: null,
      cursor: 0,
      ticksSinceLastSpawn: 0,
      completed: false,
      completesTo: null,
      output: createPort(1),
    };
    if (request.kind === 'from') state.flags.everPlacedFrom = true;
    if (request.kind === 'interval') state.flags.everPlacedInterval = true;
  } else if (request.type === 'subject') {
    state.subjects[id] = {
      id,
      position: pos,
      name: nextSubjectName(subjectNames(state)),
      fired: false,
    };
    state.flags.everPlacedSubject = true;
  } else if (request.type === 'subscriber') {
    state.sinks[id] = { id, position: pos, sinkType: 'cash', input: createPort(4) };
    state.base = { min: { ...pos }, max: { ...pos } };
    state.subscriber = createSubscription();
  } else {
    state.machines[id] = createMachineEntity(id, pos, request.kind, pipeDirection);
    if (request.kind === 'filter') state.flags.everPlacedFilter = true;
    if (request.kind === 'take') state.flags.everPlacedTake = true;
  }
  state.economy.cash -= buildCost(request);
  state.evalOrder = computeEvalOrder(state);
  return events.length > 0 ? { ok: true, events } : { ok: true };
}

/**
 * Checked after the redirect is tentatively applied, not before (unlike
 * {@link conveyorConflictsWithMachinePorts}), since the conveyor at `pos` already exists and
 * would otherwise be double-counted against its own stale, pre-redirect direction.
 */
function conveyorConflictsAfterRedirect(state: SimState, pos: GridPos): boolean {
  for (const dir of DIRECTIONS) {
    const neighborPos = translate(pos, dir);
    const neighbor = findEntityAt(state, neighborPos);
    if (!neighbor || (neighbor.kind !== 'machine' && neighbor.kind !== 'observable')) continue;
    if (entityPortConflict(state, neighborPos)) return true;
  }
  return false;
}

export function redirectConveyor(state: SimState, pos: GridPos, direction: Direction): boolean {
  const target = findEntityAt(state, pos);
  if (!target || target.kind !== 'conveyor') return false;

  const conveyor = state.conveyors[target.id];
  const previousDirection = conveyor.direction;
  if (previousDirection === direction) return true;

  conveyor.direction = direction;
  const otherEntry = findBaseEntryConveyor(state);
  const createsSecondEntry =
    isBaseEntryPoint(state, pos, direction) &&
    otherEntry !== undefined &&
    otherEntry.id !== conveyor.id;
  if (conveyorConflictsAfterRedirect(state, pos) || createsSecondEntry) {
    conveyor.direction = previousDirection;
    return false;
  }
  state.evalOrder = computeEvalOrder(state);
  return true;
}

/**
 * A Subject is named by id from two places nowhere near it on the board — the observable that
 * publishes to it and the `takeUntil` that listens — and ids are never recycled, so a reference left
 * behind names nothing for the rest of the game. Worse than useless: the tile still reads as
 * configured to its handler while reading as unconfigured everywhere the player can see.
 */
function dropReferencesToSubject(state: SimState, subjectId: EntityId): void {
  for (const observable of Object.values(state.observables)) {
    if (observable.completesTo === subjectId) observable.completesTo = null;
  }
  for (const machine of Object.values(state.machines)) {
    if (machine.kind === 'takeUntil' && machine.config?.subjectId === subjectId) {
      machine.config = null;
    }
  }
}

function deleteEntity(state: SimState, pos: GridPos): EraseResult {
  const target = findEntityAt(state, pos);
  if (!target) return { ok: false, reason: 'empty' };
  if (target.kind === 'sink') return { ok: false, reason: 'protected' };

  const events: SimEvent[] = [];
  const despawn = (packetId: PacketId) => {
    delete state.packets[packetId];
    events.push({ type: 'packetDespawned', packetId });
  };

  let refund: number;
  if (target.kind === 'conveyor') {
    const conveyor = state.conveyors[target.id];
    if (conveyor.slot !== null) despawn(conveyor.slot);
    delete state.conveyors[target.id];
    refund = CONVEYOR_COST;
  } else if (target.kind === 'observable') {
    const observable = state.observables[target.id];
    observable.output.queue.forEach(despawn);
    delete state.observables[target.id];
    refund = observableCost(observable);
  } else if (target.kind === 'subject') {
    delete state.subjects[target.id];
    dropReferencesToSubject(state, target.id);
    refund = SUBJECT_COST;
  } else {
    const machine = state.machines[target.id];
    releaseMachinePackets(machine).forEach(despawn);
    delete state.machines[target.id];
    refund = machineCost(machine);
    if (machine.pipeDirection) {
      const conveyorId = state.nextEntityId++;
      state.conveyors[conveyorId] = {
        id: conveyorId,
        position: pos,
        direction: machine.pipeDirection,
        slot: null,
      };
    }
  }

  state.economy.cash += refund;
  return { ok: true, refund, events };
}

export function erase(state: SimState, pos: GridPos): EraseResult {
  const result = deleteEntity(state, pos);
  if (!result.ok) return result;
  state.evalOrder = computeEvalOrder(state);
  return result;
}

export function clearAll(state: SimState): { refund: number; events: SimEvent[] } {
  let refund = 0;
  const events: SimEvent[] = [];
  while (Object.keys(state.conveyors).length > 0 || Object.keys(state.machines).length > 0) {
    const positions = [
      ...Object.values(state.conveyors).map((c) => c.position),
      ...Object.values(state.machines).map((m) => m.position),
    ];
    for (const pos of positions) {
      const result = deleteEntity(state, pos);
      if (result.ok) {
        refund += result.refund;
        events.push(...result.events);
      }
    }
  }
  state.evalOrder = computeEvalOrder(state);
  return { refund, events };
}

export function removeBase(state: SimState): EraseResult {
  if (!state.base || !state.subscriber) return { ok: false, reason: 'empty' };

  const events: SimEvent[] = [];
  if (state.subscribed) events.push(...toggleSubscribe(state));

  const sinkId = Object.values(state.sinks)[0]?.id;
  if (sinkId !== undefined) delete state.sinks[sinkId];

  state.base = null;
  state.subscriber = null;
  state.evalOrder = computeEvalOrder(state);

  state.economy.cash += SUBSCRIBER_COST;
  return { ok: true, refund: SUBSCRIBER_COST, events };
}
